import { useState } from 'react'

// Mock data - in production this comes from API
const MOCK_ORG = {
  name: 'Acme Corporation',
  domain: 'acme.com',
  region: 'us-west-2',
  timezone: 'America/Los_Angeles',
  currency: 'USD',
  fiscalYearStart: 'January',
}

const REGIONS = [
  { id: 'us-west-2', name: 'US West (Oregon)', flag: '🇺🇸' },
  { id: 'us-east-1', name: 'US East (Virginia)', flag: '🇺🇸' },
  { id: 'eu-west-1', name: 'Europe (Ireland)', flag: '🇪🇺' },
  { id: 'eu-central-1', name: 'Europe (Frankfurt)', flag: '🇩🇪' },
  { id: 'ap-southeast-1', name: 'Asia Pacific (Singapore)', flag: '🇸🇬' },
  { id: 'ap-northeast-1', name: 'Asia Pacific (Tokyo)', flag: '🇯🇵' },
]

const TIMEZONES = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
]

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  )
}

function InformationCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
  )
}

export default function OrganizationSettings() {
  const [org, setOrg] = useState(MOCK_ORG)
  const [hasChanges, setHasChanges] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const handleChange = (field: string, value: string) => {
    setOrg(prev => ({ ...prev, [field]: value }))
    setHasChanges(true)
    setSaved(false)
  }

  const handleSave = async () => {
    setSaving(true)
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 800))
    setSaving(false)
    setHasChanges(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  return (
    <div className="divide-y divide-surface-100">
      {/* Header */}
      <div className="p-6 sm:p-8">
        <h2 className="text-lg font-semibold text-surface-900">Organization</h2>
        <p className="mt-1 text-surface-600">
          Your company's identity and data residency preferences.
        </p>
      </div>

      {/* Company Name */}
      <div className="p-6 sm:p-8">
        <div className="max-w-2xl">
          <label htmlFor="org-name" className="block text-sm font-medium text-surface-900">
            Company name
          </label>
          <p className="mt-1 text-sm text-surface-500">
            This appears in evaluation reports and candidate communications.
          </p>
          <input
            type="text"
            id="org-name"
            value={org.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className="mt-3 block w-full rounded-lg border border-surface-300 px-4 py-2.5 text-surface-900 placeholder:text-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
            placeholder="Your company name"
          />
        </div>
      </div>

      {/* Domain */}
      <div className="p-6 sm:p-8">
        <div className="max-w-2xl">
          <label htmlFor="org-domain" className="block text-sm font-medium text-surface-900">
            Primary domain
          </label>
          <p className="mt-1 text-sm text-surface-500">
            Used for email verification and single sign-on configuration.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <input
              type="text"
              id="org-domain"
              value={org.domain}
              onChange={(e) => handleChange('domain', e.target.value)}
              className="block flex-1 rounded-lg border border-surface-300 px-4 py-2.5 text-surface-900 placeholder:text-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
              placeholder="yourcompany.com"
            />
            <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-medium">
              <CheckIcon className="w-4 h-4" />
              Verified
            </span>
          </div>
        </div>
      </div>

      {/* Data Region */}
      <div className="p-6 sm:p-8">
        <div className="max-w-2xl">
          <label htmlFor="org-region" className="block text-sm font-medium text-surface-900">
            Data region
          </label>
          <p className="mt-1 text-sm text-surface-500">
            Where your hiring data is stored and processed. Cannot be changed after setup.
          </p>
          <div className="mt-3 relative">
            <select
              id="org-region"
              value={org.region}
              disabled
              className="block w-full appearance-none rounded-lg border border-surface-200 bg-surface-50 px-4 py-2.5 text-surface-600 cursor-not-allowed"
            >
              {REGIONS.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.flag} {region.name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
              <svg className="w-4 h-4 text-surface-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
              </svg>
            </div>
          </div>
          <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-surface-50 border border-surface-200">
            <InformationCircleIcon className="w-5 h-5 text-surface-400 shrink-0 mt-0.5" />
            <p className="text-sm text-surface-600">
              Data region is locked after initial setup. Contact support if you need to migrate to a different region.
            </p>
          </div>
        </div>
      </div>

      {/* Timezone */}
      <div className="p-6 sm:p-8">
        <div className="max-w-2xl">
          <label htmlFor="org-timezone" className="block text-sm font-medium text-surface-900">
            Default timezone
          </label>
          <p className="mt-1 text-sm text-surface-500">
            Used for scheduling and displaying dates in reports.
          </p>
          <div className="mt-3 relative">
            <select
              id="org-timezone"
              value={org.timezone}
              onChange={(e) => handleChange('timezone', e.target.value)}
              className="block w-full appearance-none rounded-lg border border-surface-300 px-4 py-2.5 text-surface-900 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors cursor-pointer"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace('_', ' ')}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
              <svg className="w-4 h-4 text-surface-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="p-6 sm:p-8 bg-surface-50">
        <div className="flex items-center justify-between">
          <div className="text-sm text-surface-500">
            {hasChanges && 'You have unsaved changes'}
            {saved && (
              <span className="flex items-center gap-1.5 text-emerald-600">
                <CheckIcon className="w-4 h-4" />
                Changes saved
              </span>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
              hasChanges && !saving
                ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-sm'
                : 'bg-surface-200 text-surface-400 cursor-not-allowed'
            }`}
          >
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

